import * as React from 'react';
import { REFERENCE_CALENDAR_API } from 'constants/api';
import {
  MarketCalendarResponse,
  MarketEarlyClose,
  isMarketCalendarResponse,
} from 'constants/payloads/marketCalendar';
import {
  setPublishedMarketClosures,
  setPublishedMarketEarlyCloses,
} from 'utils/shared/time/marketSession';

export type MarketCalendarStatus = 'loading' | 'success' | 'error';

export interface MarketCalendarContextValue {
  status: MarketCalendarStatus;
  /** ET dates (YYYY-MM-DD) the market is/was fully closed. */
  closed: string[];
  /** Half-days — still trading days, but the session ends early. */
  earlyCloses: MarketEarlyClose[];
  /** Regular session close as minutes since ET midnight (e.g. 960 = 16:00), or null. */
  regularCloseMin: number | null;
  /** False until the upstream vendor calendar has loaded on the origin. */
  loaded: boolean;
}

interface Props {
  children: React.ReactNode;
}

const DEFAULT_VALUE: MarketCalendarContextValue = {
  status: 'loading',
  closed: [],
  earlyCloses: [],
  regularCloseMin: null,
  loaded: false,
};

const MarketCalendarContext = React.createContext<MarketCalendarContextValue>(DEFAULT_VALUE);

/** A single subrequest must never hang the poll — the calendar is edge-cached and answers fast. */
const REQUEST_TIMEOUT_MS = 5_000;

/**
 * Refetch cadence. The calendar changes at most a few times a year and the Worker edge-caches it
 * for hours, so this is deliberately long — its jobs are to cross a day boundary in a tab left open
 * overnight and to pick up a same-day ad-hoc closure within a few hours of it being announced,
 * neither of which needs a tight loop.
 */
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * The one exchange-calendar lookup for the app. Fetches full closures and early closes from the
 * reference Worker (`/v1/reference/calendar`) once on mount and then on a long interval, installs
 * them into the pure session model in `usEquityCalendar`, and holds the result for any component
 * that wants to read it directly.
 *
 * The install is the load-bearing side effect: `marketSessionPhase`, `usEquityCloseMinutes` and the
 * planning helpers are pure functions that read module state, so every status dot and session
 * label on the page picks up the published calendar on its next tick regardless of where it mounts.
 *
 * Fails safe. Until the first response lands — and through any later outage — classification runs on
 * the computed rules, which are correct for every scheduled holiday and wrong only for closures
 * nobody can derive. A transient failure never blanks a calendar we already installed: the module's
 * setters ignore empty payloads and this provider keeps the last good value rather than reopening a
 * closed day.
 */
export default function MarketCalendarProvider({ children }: Props) {
  const [value, setValue] = React.useState<MarketCalendarContextValue>(DEFAULT_VALUE);

  React.useEffect(() => {
    let cancelled = false;
    let controller: AbortController | null = null;

    const install = (payload: MarketCalendarResponse) => {
      // Feed the pure session model first — this is what the status dot actually reads.
      setPublishedMarketClosures(payload.closed);
      setPublishedMarketEarlyCloses(
        payload.earlyCloses.map((early) => [early.date, early.closeMin]),
      );
      setValue({
        status: 'success',
        closed: payload.closed,
        earlyCloses: payload.earlyCloses,
        regularCloseMin: payload.regularCloseMin,
        loaded: payload.loaded,
      });
    };

    const load = async () => {
      // Supersede any still-in-flight poll before starting the next one.
      controller?.abort();
      controller = new AbortController();
      const active = controller;
      const timeoutId = window.setTimeout(() => active.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(REFERENCE_CALENDAR_API, {
          cache: 'no-store',
          credentials: 'omit',
          signal: active.signal,
        });
        if (!response.ok) throw new Error(`Market calendar failed: ${response.status}`);

        const payload: unknown = await response.json();
        if (!isMarketCalendarResponse(payload)) throw new Error('Invalid market calendar response');

        if (!cancelled) install(payload);
      } catch {
        // A missing calendar is not a user-facing condition — the computed rules keep the dot
        // working. Only surface `error` if we never installed a good calendar; once we have one, a
        // later failure leaves it untouched rather than doubting our own data on the tick after.
        if (!cancelled) {
          setValue((prev) =>
            prev.status === 'success' ? prev : { ...DEFAULT_VALUE, status: 'error' },
          );
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    load();
    const intervalId = window.setInterval(load, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller?.abort();
      window.clearInterval(intervalId);
    };
  }, []);

  return <MarketCalendarContext.Provider value={value}>{children}</MarketCalendarContext.Provider>;
}

export const useMarketCalendar = (): MarketCalendarContextValue =>
  React.useContext(MarketCalendarContext);
